import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import HowToRegIcon from "@mui/icons-material/HowToReg";

import { PERMISSIONS, APPLICANT_DOC_STATUS } from "../../constants";
import {
  APPLICATION_STATUS_LABELS,
  ESTADO_DOC_LABELS,
  ID_TYPE_SHORT,
  labelForStatus,
} from "./applicantLabels";
import { applicantScreening } from "./identityCheck";
import { ScreeningChip } from "./IdentityNotice";
import {
  applicantDocPublicUrl,
  fetchApplicantPage,
  fetchApplicationPage,
} from "../../services/backend/ApplicantService";
import {
  allApplications,
  getApplicantConfig,
  DEFAULT_APPLICANT_CONFIG,
} from "../../services/backend/ApplicantConfigService";
import {
  appendApplicantList,
  setApplicantList,
  setApplicantLoading,
  setApplicantFilters,
} from "../../redux/slices/applicantSlice";

const PAGE_SIZE = 25;
const CASCADE_BATCH = 50;

const ESTADO_DOC_COLOR = {
  [APPLICANT_DOC_STATUS.PENDING]: "default",
  [APPLICANT_DOC_STATUS.INCOMPLETE]: "warning",
  [APPLICANT_DOC_STATUS.COMPLETE]: "info",
  [APPLICANT_DOC_STATUS.VALIDATED]: "success",
};

// Un fallo de la consulta no puede verse igual que "no hay resultados": cada
// código de error de Firestore se traduce a algo accionable.
const LOAD_ERROR_MESSAGE = {
  "failed-precondition":
    "La consulta necesita un índice de Firestore que aún se está construyendo. Espera unos minutos y vuelve a recargar.",
  "permission-denied":
    "No tienes permiso para consultar estos datos. Revisa las reglas de Firestore del proyecto.",
};

// Estado de la postulación → color del Chip (sólo en la vista por convocatoria).
const APPLICATION_STATUS_COLOR = {
  SELECTED: "success",
  REJECTED: "error",
  DOCS_STAGE2_PENDING: "warning",
  IN_REVIEW: "info",
};

// Fila resaltada para los aspirantes ya seleccionados como contratistas: fondo
// verde tenue + franja lateral, para distinguirlos de un vistazo en la lista.
const SELECTED_ROW_SX = {
  bgcolor: (t) => alpha(t.palette.success.main, 0.08),
  "&.MuiTableRow-hover:hover": { bgcolor: (t) => alpha(t.palette.success.main, 0.16) },
  "& td:first-of-type": { borderLeft: (t) => `3px solid ${t.palette.success.main}` },
};

// Fila de un aspirante que no pasó la revisión automática (algún documento no
// parece suyo): roja entera, por encima del verde de seleccionado.
const FLAGGED_ROW_SX = {
  bgcolor: (t) => alpha(t.palette.error.main, 0.1),
  "&.MuiTableRow-hover:hover": { bgcolor: (t) => alpha(t.palette.error.main, 0.18) },
  "& td:first-of-type": { borderLeft: (t) => `3px solid ${t.palette.error.main}` },
};

function formatDate(v) {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
}

const initials = (a) =>
  `${(a.firstName || "").charAt(0)}${(a.lastName || "").charAt(0)}`.toUpperCase();

export default function ApplicantList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector((state) => state.user.value);
  const { list, loading, filters } = useSelector((state) => state.applicant);
  const [search, setSearch] = useState("");
  // Revisión automática (identityCheck): "" | PASSED | FLAGGED | INCOMPLETE | NONE.
  // Filtered on the loaded rows, like the search, so it works in both the
  // general and the per-call list without an extra index.
  const [screeningFilter, setScreeningFilter] = useState("");
  const [cascadeCap, setCascadeCap] = useState(CASCADE_BATCH);
  // El aspirante guarda un *path* de foto, no una URL: hace falta el bucket.
  const [storageBucket, setStorageBucket] = useState(DEFAULT_APPLICANT_CONFIG.storageBucket);
  // Catálogo de convocatorias; sólo se ofrece el filtro si hay más que General.
  const [calls, setCalls] = useState([]);
  // Los parámetros de la URL (?call, ?status) se aplican antes de la primera
  // consulta, para no pedir una página que se va a descartar.
  const [ready, setReady] = useState(false);
  // Código de error de la última consulta (null = todo bien).
  const [loadError, setLoadError] = useState(null);

  const callId = filters.callId || null;
  const currentCall = useMemo(
    () => calls.find((c) => c.id === callId) || null,
    [calls, callId]
  );
  // Una columna extra ("Postulación") sólo en la vista por convocatoria.
  const columnCount = callId ? 8 : 7;

  const cascadeMode = !!search || !!screeningFilter;
  const perms = user?.permissions || [];
  const allowed = perms.includes(PERMISSIONS.APPLICANT.LIST);

  const cursorRef = useRef(null);
  const inFlightRef = useRef(false);
  const generationRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const dateFrom = useMemo(() => {
    if (!filters.dateFrom) return null;
    const d = new Date(`${filters.dateFrom}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  }, [filters.dateFrom]);
  const dateTo = useMemo(() => {
    if (!filters.dateTo) return null;
    const d = new Date(`${filters.dateTo}T23:59:59.999`);
    return isNaN(d.getTime()) ? null : d;
  }, [filters.dateTo]);

  const loadPage = useCallback(
    async (reset) => {
      if (!reset && inFlightRef.current) return;
      if (reset) {
        generationRef.current += 1;
        cursorRef.current = null;
        setHasMore(true);
        setCascadeCap(CASCADE_BATCH);
        dispatch(setApplicantLoading(true));
      } else {
        setLoadingMore(true);
      }
      const myGen = generationRef.current;
      inFlightRef.current = true;
      try {
        // Acotado a una convocatoria el listado se arma desde las postulaciones
        // (collection group), para reflejar el avance de ESA convocatoria.
        const fetchPage = callId
          ? (args) => fetchApplicationPage({ callId, ...args })
          : fetchApplicantPage;
        const { items, lastDoc, hasMore: more, error } = await fetchPage({
          documentationStatus: filters.documentationStatus,
          dateFrom,
          dateTo,
          cursor: cursorRef.current,
          pageSize: PAGE_SIZE,
        });
        if (myGen !== generationRef.current) return;
        setLoadError(error || null);
        cursorRef.current = lastDoc;
        setHasMore(more);
        if (reset) dispatch(setApplicantList(items));
        else dispatch(appendApplicantList(items));
      } finally {
        if (myGen === generationRef.current) {
          inFlightRef.current = false;
          setLoadingMore(false);
          if (reset) dispatch(setApplicantLoading(false));
        }
      }
    },
    [dispatch, callId, filters.documentationStatus, dateFrom, dateTo]
  );

  // La URL manda sobre el alcance: sin ?call se listan todos los aspirantes (así
  // "Todos los aspirantes" nunca hereda una convocatoria de una visita anterior).
  // ?status sólo se aplica cuando viene, para no pisar el filtro que el gestor
  // dejó puesto al navegar a una ficha y volver.
  useEffect(() => {
    const status = searchParams.get("status");
    dispatch(setApplicantFilters({
      callId: searchParams.get("call") || null,
      ...(status ? { documentationStatus: status } : {}),
    }));
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!allowed) {
      navigate("/home");
      return;
    }
    if (!ready) return;
    loadPage(true);
  }, [allowed, navigate, ready, loadPage]);

  useEffect(() => {
    let alive = true;
    getApplicantConfig().then((c) => {
      if (!alive) return;
      setStorageBucket(c.storageBucket);
      setCalls(allApplications(c));
    });
    return () => { alive = false; };
  }, []);

  // Cambiar de convocatoria reescribe la URL: la vista queda compartible y el
  // "Volver" desde la ficha regresa al mismo alcance.
  const changeCall = useCallback(
    (id) => {
      dispatch(setApplicantFilters({ callId: id || null }));
      const next = new URLSearchParams(searchParams);
      if (id) next.set("call", id);
      else next.delete("call");
      setSearchParams(next, { replace: true });
    },
    [dispatch, searchParams, setSearchParams]
  );

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || !hasMore || inFlightRef.current) return;
        if (cascadeMode) {
          if (list.length >= cascadeCap) setCascadeCap(list.length + CASCADE_BATCH);
        } else {
          loadPage(false);
        }
      },
      { rootMargin: "200px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadPage, list.length, cascadeMode, cascadeCap]);

  const cascading = cascadeMode && hasMore && list.length < cascadeCap;
  useEffect(() => {
    if (!cascading || inFlightRef.current) return;
    loadPage(false);
  }, [cascading, list.length, loadingMore, loadPage]);

  useEffect(() => {
    setCascadeCap(CASCADE_BATCH);
  }, [search, screeningFilter]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return list.filter((a) => {
      if (search && ![a.nuip, a.firstName, a.lastName, a.email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))) return false;
      if (screeningFilter) {
        const status = applicantScreening(a)?.status || "NONE";
        if (status !== screeningFilter) return false;
      }
      return true;
    });
  }, [list, search, screeningFilter]);

  const statusMessage = useMemo(() => {
    if (search || screeningFilter) {
      if (cascading) return `Buscando… ${list.length} revisados · ${filtered.length} coincidencias`;
      if (!hasMore) return `Búsqueda completa: ${filtered.length} coincidencias en ${list.length} resultados.`;
      return `Mostrando los primeros ${list.length} resultados. Desplázate para revisar más.`;
    }
    return "Filtra los resultados cargados. Desplázate para cargar más.";
  }, [search, screeningFilter, cascading, hasMore, list.length, filtered.length]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {callId ? `Aspirantes · ${currentCall?.name || callId}` : "Aspirantes"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {callId
          ? "Aspirantes postulados a esta convocatoria. El avance y la documentación corresponden a la postulación, no al registro general."
          : "Banco de postulantes. Consulta hojas de vida, valida documentación y selecciona aspirantes como contratistas."}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Buscar cédula, nombre, email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          {calls.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Convocatoria</InputLabel>
              <Select
                label="Convocatoria"
                value={callId || ""}
                onChange={(e) => changeCall(e.target.value)}
              >
                <MenuItem value="">Todas las convocatorias</MenuItem>
                {calls.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name || c.id}{c.active === false ? " (inactiva)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Documentación</InputLabel>
            <Select
              label="Documentación"
              value={filters.documentationStatus || ""}
              onChange={(e) => dispatch(setApplicantFilters({ documentationStatus: e.target.value || null }))}
            >
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(ESTADO_DOC_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Revisión automática</InputLabel>
            <Select
              label="Revisión automática"
              value={screeningFilter}
              onChange={(e) => setScreeningFilter(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="FLAGGED">No pasó</MenuItem>
              <MenuItem value="INCOMPLETE">Incompleta</MenuItem>
              <MenuItem value="PASSED">Sin alertas</MenuItem>
              <MenuItem value="NONE">Sin documentos</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small" type="date" label="Desde"
            value={filters.dateFrom || ""}
            onChange={(e) => dispatch(setApplicantFilters({ dateFrom: e.target.value || null }))}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
          />
          <TextField
            size="small" type="date" label="Hasta"
            value={filters.dateTo || ""}
            onChange={(e) => dispatch(setApplicantFilters({ dateTo: e.target.value || null }))}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
          />
          {(filters.dateFrom || filters.dateTo) && (
            <Tooltip title="Limpiar fechas">
              <span>
                <IconButton size="small" onClick={() => dispatch(setApplicantFilters({ dateFrom: null, dateTo: null }))}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Recargar">
            <span>
              <IconButton size="small" onClick={() => loadPage(true)} disabled={loading || loadingMore} sx={{ ml: { sm: "auto" } }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Typography variant="caption" color={cascading ? "primary" : "text.secondary"} sx={{ display: "block", mt: 1 }}>
          {statusMessage}
        </Typography>
      </Paper>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {LOAD_ERROR_MESSAGE[loadError] ||
            "No fue posible cargar el listado. Inténtalo de nuevo."}
        </Alert>
      )}

      <Paper sx={{ position: "relative", overflow: "hidden" }}>
        {(cascading || loadingMore) && (
          <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 2 }} />
        )}
        {loading && !list.length ? (
          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ "& .MuiTableCell-head": { fontWeight: 700 } }}>
                <TableRow>
                  <TableCell>Documento</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Completitud</TableCell>
                  <TableCell>Documentación</TableCell>
                  <TableCell>Revisión automática</TableCell>
                  {callId && <TableCell>Postulación</TableCell>}
                  <TableCell>{callId ? "Postulado" : "Registrado"}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((a) => {
                  const seleccionado = a.selected === true;
                  const noPaso = applicantScreening(a)?.status === "FLAGGED";
                  const nombre = `${a.firstName || ""} ${a.lastName || ""}`.trim();
                  return (
                  <TableRow
                    key={a.nuip}
                    hover
                    onClick={() => navigate(
                      `/applicants/${encodeURIComponent(a.nuip)}${callId ? `?call=${encodeURIComponent(callId)}` : ""}`
                    )}
                    sx={{ cursor: "pointer", ...(seleccionado ? SELECTED_ROW_SX : {}), ...(noPaso ? FLAGGED_ROW_SX : {}) }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {a.idType && (
                        <Tooltip title={a.idType}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {labelForStatus(ID_TYPE_SHORT, a.idType, "")}
                          </Typography>
                        </Tooltip>
                      )}
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{a.nuip}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Avatar
                          alt={nombre}
                          src={applicantDocPublicUrl(storageBucket, a.photoPath) || undefined}
                          sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "primary.main" }}
                        >
                          {initials(a) || "?"}
                        </Avatar>
                        <span>{nombre || "—"}</span>
                        {/* La postulación exacta se ve en la ficha; aquí basta la fecha
                            (selectedCall guarda el id crudo de la convocatoria). */}
                        {seleccionado && !callId && (
                          <Tooltip
                            title={`Seleccionado como contratista${a.selectedAt ? ` · ${formatDate(a.selectedAt)}` : ""}`}
                          >
                            <Chip
                              icon={<HowToRegIcon />}
                              label={APPLICATION_STATUS_LABELS.SELECTED}
                              color="success"
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{a.email || "—"}</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(0, Math.min(100, a.completionPercentage || 0))}
                          sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption">{a.completionPercentage || 0}%</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={labelForStatus(ESTADO_DOC_LABELS, a.documentationStatus)}
                        color={ESTADO_DOC_COLOR[a.documentationStatus] || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {applicantScreening(a)?.status
                        ? <ScreeningChip screening={applicantScreening(a)} compact />
                        : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    {callId && (
                      <TableCell>
                        <Chip
                          label={labelForStatus(APPLICATION_STATUS_LABELS, a.applicationStatus)}
                          color={APPLICATION_STATUS_COLOR[a.applicationStatus] || "default"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    <TableCell>{formatDate(a.createdAt)}</TableCell>
                  </TableRow>
                  );
                })}
                {filtered.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={columnCount} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {callId
                          ? "Sin aspirantes postulados a esta convocatoria para los filtros aplicados."
                          : "Sin aspirantes para los filtros aplicados."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {hasMore && (
                  <TableRow>
                    <TableCell colSpan={columnCount} ref={sentinelRef} sx={{ py: 2, border: 0 }}>
                      {loadingMore && (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
}
